import React from 'react'
import { Control, Controller, ControllerRenderProps } from 'react-hook-form'
import { Box, IconButton } from '@material-ui/core'
import { DateTimePicker } from '@material-ui/pickers'
import { addHours, differenceInHours, setMinutes, subHours } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { hoursInYear } from '../../util/hours-in-year'

type Props = {
  control: Control
  name: string
  label: string
  startOfYearDate: Date
  defaultValue?: Date
  onChange?: () => void
}

/**
 * This component is meant to be used to choose the correct date and hour out of the 8760 hours of the year.
 * Due to limitations of @material-ui/pickers the UI also enables choosing the minute, but the minute is not used anywhere.
 */
export function ControlledHourPicker(props: Props): React.ReactElement {
  const {
    control,
    name,
    label,
    startOfYearDate,
    defaultValue,
    onChange: onHourPickerChange,
  } = props
  const { t } = useTranslation()

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue ?? new Date()}
      render={({
        value,
        onChange: onControllerChange,
      }: // eslint-disable-next-line react/no-unused-prop-types
      Omit<ControllerRenderProps, 'value'> & { value: Date }) => {
        const onChange = (newValue: Date) => {
          onControllerChange(newValue)

          if (onHourPickerChange) {
            onHourPickerChange()
          }
        }

        return (
          <Box display="flex">
            <DateTimePicker
              size="small"
              fullWidth={false}
              format={t('date.short')}
              value={value}
              onChange={(newValue) =>
                onChange(newValue ? setMinutes(newValue, 0) : startOfYearDate)
              }
              shouldDisableDate={(date) => {
                if (!date) {
                  return true
                }

                const hour = differenceInHours(date, startOfYearDate)

                return hour < 0 || hour >= hoursInYear
              }}
              ampm={false}
              minutesStep={60}
              label={label}
            />

            <Box ml={1}>
              <IconButton
                onClick={() => onChange(subHours(value, 1))}
                disabled={
                  differenceInHours(subHours(value, 1), startOfYearDate) < 0
                }
              >
                <ChevronLeft />
              </IconButton>

              <IconButton
                onClick={() => onChange(addHours(value, 1))}
                disabled={
                  differenceInHours(addHours(value, 1), startOfYearDate) >=
                  hoursInYear
                }
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>
        )
      }}
    />
  )
}
